import { useEffect, useLayoutEffect, useState } from 'react'
import { getRouteApi } from '@tanstack/react-router'
import { useAppStore } from '@/lib/app-store'

const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect

const rootApi = getRouteApi('__root__')

/**
 * The resolved light/dark theme, safe to use for theme-conditional *content* that can't be expressed
 * with a CSS `dark:` variant — e.g. picking a video `src`/`poster` per theme.
 *
 * Returns `null` when the theme isn't known yet: in system mode the server has no `theme` cookie and
 * can't read the OS preference, so it can't pick a variant. Consumers should render a neutral
 * placeholder for `null` rather than guessing — otherwise the server paints the light variant and it
 * flashes to dark after hydration (the painted `dark` class, set pre-paint by the manageTheme script,
 * can fix CSS but not a video `src`).
 *
 * Why not `store.resolvedDarkMode`? That value is `'light'` at SSR and the first client render (the
 * AppShell sync only corrects it post-hydration), so a theme-conditional asset flashes. And seeding
 * the store from the painted class breaks hydration: the server singleton can't read the per-request
 * cookie, so it renders `'light'` while the client reads `'dark'` → mismatch.
 *
 * Instead this seeds from the root loader's `theme` (resolved server-side from the cookie and
 * dehydrated to the client, so SSR and the first client render agree → no mismatch; an explicit dark
 * cookie renders the dark variant on the server with no flash, and system mode is `null` on both),
 * then — after mount, before paint — follows the actual `dark` class on `<html>` so system preference
 * and runtime toggles are reflected. The `resolvedDarkMode` dependency is the signal that the AppShell
 * sync ran (and updated the class).
 */
export function useResolvedTheme(): 'light' | 'dark' | null {
  const { theme } = rootApi.useLoaderData()
  const storeResolved = useAppStore((state) => state.store.resolvedDarkMode)
  const [resolved, setResolved] = useState<'light' | 'dark' | null>(
    theme === 'dark' ? 'dark' : theme === 'light' ? 'light' : null,
  )

  useIsomorphicLayoutEffect(() => {
    setResolved(document.documentElement.classList.contains('dark') ? 'dark' : 'light')
  }, [storeResolved])

  return resolved
}
